import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import ManagementHeader from './ManagementHeader';
import { fetchEquipmentsForManagement, deleteEquipment } from '../../services/api';
import { hasPermission } from '../../services/authService';
import { useTranslation } from '../../context/LanguageContext';
import { EQUIPMENT_STATUS_KO, EQUIPMENT_STATUS_BADGE } from './equipmentStatus';
import './management.css';

const DASH = '—';

/* GET /management/equipments 응답 → 화면 행. 값이 없으면 null 로 두고 화면에서 — 로 표시한다 */
const toRow = (equipment) => ({
    id: equipment.id,
    name: equipment.equipmentName,
    code: equipment.equipmentCode,
    model: equipment.model || null,
    line: equipment.lineName || null,
    manufacturer: equipment.manufacturer || null,
    serialNumber: equipment.serialNumber || null,
    status: EQUIPMENT_STATUS_KO[equipment.status] || '대기',
    maxTemperature: equipment.maxTemperature ? `${equipment.maxTemperature}°C` : null,
    maxPressure: equipment.maxPressure ? `${equipment.maxPressure}MPa` : null,
    cycleTime: equipment.targetCycleTime ? `${(equipment.targetCycleTime / 1000).toFixed(1)}s` : null,
    cavityCount: equipment.cavityCount || null,
    lastInspection: equipment.lastInspectionDate || null,
    factory: equipment.factoryName || null,
    factoryId: equipment.factoryId,
    lineId: equipment.lineId
});

function EquipmentListPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedStatus, setSelectedStatus] = useState('전체');
    const [selectedFactory, setSelectedFactory] = useState('전체');
    const [selectedLine, setSelectedLine] = useState('전체');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedEquipment, setSelectedEquipment] = useState(null);
    const [equipments, setEquipments] = useState([]);
    const [allEquipments, setAllEquipments] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    // 권한 체크
    const canCreate = hasPermission('equipment.create');
    const canUpdate = hasPermission('equipment.update');
    const canDelete = hasPermission('equipment.delete');

    const loadEquipments = useCallback(async () => {
        const data = await fetchEquipmentsForManagement();
        const rows = (Array.isArray(data) ? data : []).map(toRow);
        setAllEquipments(rows);
        setEquipments(rows);
    }, []);

    // API에서 설비 목록 가져오기
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                await loadEquipments();
            } catch (err) {
                console.error('설비 목록 조회 실패:', err);
                setAllEquipments([]);
                setEquipments([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [loadEquipments]);

    // 필터 선택지 (중복 제거, 값이 없는 행 제외)
    const factories = useMemo(() => [...new Set(allEquipments.map(e => e.factory).filter(Boolean))].sort(), [allEquipments]);
    const lines = useMemo(() => [...new Set(allEquipments.map(e => e.line).filter(Boolean))].sort(), [allEquipments]);

    const getStatusLabel = (status) => {
        const map = {
            '가동': t('pages.management.equipmentList.status.running'),
            '대기': t('pages.management.equipmentList.status.idle'),
            '정지': t('pages.management.equipmentList.status.stopped'),
            '점검': t('pages.management.equipmentList.status.maintenance'),
            '고장': t('pages.management.equipmentList.status.fault'),
            '오프라인': t('pages.management.equipmentList.status.offline'),
        };
        return map[status] || status;
    };

    // 필터링 로직
    useEffect(() => {
        let filtered = [...allEquipments];

        if (selectedFactory !== '전체') {
            filtered = filtered.filter(equipment => equipment.factory === selectedFactory);
        }
        if (selectedLine !== '전체') {
            filtered = filtered.filter(equipment => equipment.line === selectedLine);
        }
        if (selectedStatus !== '전체') {
            filtered = filtered.filter(equipment => equipment.status === selectedStatus);
        }
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(equipment =>
                (equipment.name || '').toLowerCase().includes(query) ||
                (equipment.model || '').toLowerCase().includes(query) ||
                (equipment.code || '').toLowerCase().includes(query)
            );
        }

        setEquipments(filtered);
        setPage(0); // 필터 변경 시 첫 페이지로
    }, [selectedFactory, selectedLine, selectedStatus, searchQuery, allEquipments]);

    // 페이지네이션 적용
    const paginatedEquipments = useMemo(() => {
        const startIndex = page * size;
        return equipments.slice(startIndex, startIndex + size);
    }, [equipments, page, size]);

    const totalElements = equipments.length;
    const totalPages = Math.ceil(totalElements / size);

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const handleDeleteClick = (equipment) => {
        setSelectedEquipment(equipment);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await deleteEquipment(selectedEquipment.id);
            alert(t('pages.management.equipmentList.alertDeleted', { name: selectedEquipment.name }));
            setIsDeleteModalOpen(false);
            await loadEquipments(); // 목록 새로고침
        } catch (error) {
            console.error('설비 삭제 실패:', error);
            alert(t('pages.management.equipmentList.alertDeleteFail'));
        }
    };

    const showActions = canUpdate || canDelete;
    const colCount = showActions ? 13 : 12;
    const cell = (v) => (v == null || v === '' ? <span className="mg-muted">{DASH}</span> : v);

    return (
        <MainLayout>
            <div className="mg-page">
                <ManagementHeader crumbs={[{ label: t('sidebar.management.equipmentList') }]}>
                    {canCreate && (
                        <button type="button" className="mg-btn primary" onClick={() => navigate('/equipment/register')}>
                            <span className="material-symbols-outlined">add</span>
                            {t('pages.management.equipmentList.addBtn')}
                        </button>
                    )}
                </ManagementHeader>

                <div className="mg-panel fill">
                    <div className="mg-toolbar">
                        <label className="mg-field">
                            {t('pages.management.equipmentList.label.factory')}
                            <select className="mg-select" value={selectedFactory} onChange={(e) => setSelectedFactory(e.target.value)}>
                                <option value="전체">{t('pages.management.equipmentList.statusOption.all')}</option>
                                {factories.map(factory => (
                                    <option key={factory} value={factory}>{factory}</option>
                                ))}
                            </select>
                        </label>
                        <label className="mg-field">
                            {t('pages.management.equipmentList.label.line')}
                            <select className="mg-select" value={selectedLine} onChange={(e) => setSelectedLine(e.target.value)}>
                                <option value="전체">{t('pages.management.equipmentList.statusOption.all')}</option>
                                {lines.map(line => (
                                    <option key={line} value={line}>{line}</option>
                                ))}
                            </select>
                        </label>
                        <label className="mg-field">
                            {t('pages.management.equipmentList.label.status')}
                            <select className="mg-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                                <option value="전체">{t('pages.management.equipmentList.statusOption.all')}</option>
                                <option value="가동">{t('pages.management.equipmentList.statusOption.running')}</option>
                                <option value="대기">{t('pages.management.equipmentList.statusOption.idle')}</option>
                                <option value="점검">{t('pages.management.equipmentList.statusOption.maintenance')}</option>
                                <option value="고장">{t('pages.management.equipmentList.statusOption.fault')}</option>
                            </select>
                        </label>
                        <div className="mg-toolbar-right">
                            <div className="mg-search">
                                <input
                                    type="text"
                                    className="mg-input"
                                    placeholder={t('pages.management.equipmentList.searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <span className="material-symbols-outlined">search</span>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="mg-loading">{t('pages.management.equipmentList.loading')}</div>
                    ) : (
                        <>
                            <div className="mg-table-wrap">
                                <table className="mg-table">
                                    <thead>
                                        <tr>
                                            <th className="left">{t('pages.management.equipmentList.col.name')}</th>
                                            <th>{t('pages.management.equipmentList.col.model')}</th>
                                            <th>{t('pages.management.equipmentList.col.factory')}</th>
                                            <th>{t('pages.management.equipmentList.col.line')}</th>
                                            <th>{t('pages.management.equipmentList.col.manufacturer')}</th>
                                            <th>{t('pages.management.equipmentList.col.serialNumber')}</th>
                                            <th>{t('pages.management.equipmentList.col.status')}</th>
                                            <th>{t('pages.management.equipmentList.col.maxTemperature')}</th>
                                            <th>{t('pages.management.equipmentList.col.maxPressure')}</th>
                                            <th>{t('pages.management.equipmentList.col.cycleTime')}</th>
                                            <th>{t('pages.management.equipmentList.col.cavity')}</th>
                                            <th>{t('pages.management.equipmentList.col.lastInspection')}</th>
                                            {showActions && <th>{t('pages.management.equipmentList.col.action')}</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedEquipments.length === 0 && (
                                            <tr className="mg-empty-row"><td className="mg-empty" colSpan={colCount}>{t('common.noData')}</td></tr>
                                        )}
                                        {paginatedEquipments.map((equipment) => (
                                            <tr key={equipment.id} onClick={() => navigate(`/equipment/${equipment.id}`)}>
                                                <td className="left">
                                                    <div className="mg-name">
                                                        <span className="mg-name-icon"><span className="material-symbols-outlined">precision_manufacturing</span></span>
                                                        <div>
                                                            <div className="mg-name-main">{equipment.name}</div>
                                                            <div className="mg-name-sub">{equipment.code}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>{equipment.model ? <span className="mg-code">{equipment.model}</span> : cell(null)}</td>
                                                <td>{cell(equipment.factory)}</td>
                                                <td>{cell(equipment.line)}</td>
                                                <td>{cell(equipment.manufacturer)}</td>
                                                <td>{cell(equipment.serialNumber)}</td>
                                                <td>
                                                    <span className={`mg-badge ${EQUIPMENT_STATUS_BADGE[equipment.status] || 'neutral'}`}>{getStatusLabel(equipment.status)}</span>
                                                </td>
                                                <td>{cell(equipment.maxTemperature)}</td>
                                                <td>{cell(equipment.maxPressure)}</td>
                                                <td>{cell(equipment.cycleTime)}</td>
                                                <td>{cell(equipment.cavityCount)}</td>
                                                <td>{cell(equipment.lastInspection)}</td>
                                                {showActions && (
                                                    <td>
                                                        <div className="mg-actions" onClick={(e) => e.stopPropagation()}>
                                                            {canUpdate && (
                                                                <button type="button" className="mg-icon-btn" onClick={() => navigate(`/equipment/${equipment.id}?edit=true`)}>
                                                                    <span className="material-symbols-outlined">edit</span>
                                                                </button>
                                                            )}
                                                            {canDelete && (
                                                                <button type="button" className="mg-icon-btn danger" onClick={() => handleDeleteClick(equipment)}>
                                                                    <span className="material-symbols-outlined">delete</span>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                totalElements={totalElements}
                                size={size}
                                onPageChange={setPage}
                                onSizeChange={handleSizeChange}
                            />
                        </>
                    )}
                </div>
            </div>

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title={t('pages.management.equipmentList.deleteModal.title')}
                message={selectedEquipment ? t('pages.management.equipmentList.deleteModal.message', { name: selectedEquipment.name }) : ''}
                confirmText={t('pages.management.equipmentList.deleteModal.confirm')}
                cancelText={t('pages.management.equipmentList.deleteModal.cancel')}
                type="danger"
            />
        </MainLayout>
    );
}

export default EquipmentListPage;
